// Worlds, sprite assets + helpers shared by the navigation grid components.
// Mirrors backend utils/nav.py (WORLDS, GOAL_COUNT, DEFAULT_FUEL, FUEL_CELL).
export const WORLDS = {
  mines: {
    id: "mines", name: "Mines", bg: "#d9bc8f", line: "rgba(0,0,0,0.18)", border: "border-stone-700",
    sprites: { vehicle: "/nav/drill.png", obstacle: "/nav/rock.png", danger: "/nav/danger.png", goal: "/nav/treasure.png", tunnel: "/nav/tunnel.png", power: "/nav/drillpower.png" },
    size: { obstacle: "w-full h-full", danger: "w-[78%] h-[78%]", goal: "w-[84%] h-[84%]", power: "w-[78%] h-[78%]" },
    labels: { vehicle: "drill", obstacle: "rock", obstacles: "rocks", danger: "danger zone", dangers: "danger zones", goal: "treasure", goals: "treasure", collectAll: "the treasure" },
    goalCount: [1, 1], fuel: false,
  },
  ocean: {
    id: "ocean", name: "Ocean", bg: "#4aa3d9", line: "rgba(255,255,255,0.22)", border: "border-sky-900",
    sprites: { vehicle: "/nav/sub_drill.png", obstacle: "/nav/coral.png", danger: "/nav/anglerfish.png", goal: "/nav/diamond.png", tunnel: "/nav/tunnel.png", power: "/nav/drillpower.png" },
    size: { obstacle: "w-[96%] h-[96%]", danger: "w-[88%] h-[88%]", goal: "w-[80%] h-[80%]", power: "w-[78%] h-[78%]" },
    labels: { vehicle: "submarine drill", obstacle: "coral", obstacles: "coral", danger: "anglerfish", dangers: "anglerfish", goal: "diamond", goals: "diamonds", collectAll: "both diamonds" },
    goalCount: [2, 2], fuel: false,
  },
  moon: {
    id: "moon", name: "Moon", bg: "#a9aab2", line: "rgba(0,0,0,0.16)", border: "border-zinc-700",
    sprites: { vehicle: "/nav/lunar_drill.png", obstacle: "/nav/moonrock.png", danger: "/nav/crack.png", goal: "/nav/sample.png", tunnel: "/nav/tunnel.png", fuel: "/nav/fuelcell.png", power: "/nav/drillpower.png" },
    size: { obstacle: "w-[92%] h-[92%]", danger: "w-full h-full", goal: "w-[80%] h-[80%]", fuel: "w-[74%] h-[74%]", power: "w-[78%] h-[78%]" },
    labels: { vehicle: "lunar drill", obstacle: "moon rock", obstacles: "moon rocks", danger: "crack", dangers: "cracks", goal: "science sample", goals: "science samples", collectAll: "every science sample" },
    goalCount: [1, 3], fuel: true,
  },
};
export const WORLD_IDS = Object.keys(WORLDS);
export const DEFAULT_FUEL = 5;
export const MAX_FUEL = 10;
export const FUEL_CELL = 10;

export const worldOf = (nav) => (WORLDS[nav?.world] ? nav.world : "mines");
export const worldMeta = (nav) => WORLDS[worldOf(nav)];

// Legacy Mines sprites (still used by admin previews / tour copy)
export const SPRITES = WORLDS.mines.sprites;
export const SOIL_COLOR = WORLDS.mines.bg;

export const DIR_DEG = { N: 0, E: 90, S: 180, W: 270 };
export const DIRS = ["N", "E", "S", "W"];
export const GRID = 10;

export const key = (x, y) => `${x},${y}`;

// Every collectable on the grid: `goals` (worlds) or the legacy single `treasure`
export const navGoals = (nav) => (nav?.goals?.length ? nav.goals : nav?.treasure ? [nav.treasure] : []);

// Index every placed item by cell for quick lookup while rendering
export const indexNav = (nav) => {
  const cells = {};
  (nav?.rocks || []).forEach((c) => { cells[key(c.x, c.y)] = { kind: "obstacle" }; });
  (nav?.dangers || []).forEach((c) => { cells[key(c.x, c.y)] = { kind: "danger" }; });
  (nav?.tunnels || []).forEach((t) => {
    if (t.a) cells[key(t.a.x, t.a.y)] = { kind: "tunnel", letter: t.letter };
    if (t.b) cells[key(t.b.x, t.b.y)] = { kind: "tunnel", letter: t.letter };
  });
  (nav?.fuel_cells || []).forEach((c) => { cells[key(c.x, c.y)] = { kind: "fuel" }; });
  (nav?.powers || []).forEach((c) => { cells[key(c.x, c.y)] = { kind: "power" }; });
  navGoals(nav).forEach((g, i) => { cells[key(g.x, g.y)] = { kind: "goal", index: i }; });
  return cells;
};

export const emptyNav = (world = "mines") => {
  const w = WORLDS[world] || WORLDS.mines;
  const goals = w.goalCount[0] === 2 ? [{ x: GRID - 1, y: 0 }, { x: GRID - 1, y: GRID - 1 }] : [{ x: GRID - 1, y: 0 }];
  return {
    cols: GRID, rows: GRID, world: w.id,
    start: { x: 0, y: GRID - 1, dir: "N" },
    treasure: goals[0], goals,
    rocks: [], dangers: [], tunnels: [],
    fuel: w.fuel ? DEFAULT_FUEL : null, fuel_cells: [], powers: [], gems: [],
    max_blocks: null, excluded_categories: [], excluded_blocks: [],
  };
};

// Shortest rotation between headings so the drill never spins the long way round
export const rotateTowards = (currentDeg, dir) => {
  const target = DIR_DEG[dir] ?? 0;
  let delta = ((target - (currentDeg % 360)) + 540) % 360 - 180;
  return currentDeg + delta;
};

// Activate-drill power-up: picked up like a goal, lets one forward move bore through one rock
export const POWER_LABEL = "Activate drill";
export const POWER_TIP = "Activate drill power-up: pick it up to turn the drill on - the next forward move bores through one rock, then the drill switches off.";

export const GEM_SPRITE = "/nav/gem.png";
export const GEM_BONUS = 10;
export const MAX_HIDDEN_GEMS = 2;
export const GEM_TIP = "Hidden gem: invisible to players. A loop or if switches the drill's scanner on, which reveals any gem in the 3x3 squares around the drill for +10 each (up to 2 per grid).";
